import React, { useMemo, useState } from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const parseRecipientList = (value) => {
  const source = Array.isArray(value) ? value : String(value || '').split(/[;,\n]+/);
  const seen = new Set();
  return source
    .flatMap((entry) => String(entry || '').split(/[;,\n]+/))
    .map((entry) => entry.trim())
    .filter((entry) => {
      const key = entry.toLowerCase();
      if (!entry || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 25);
};

export const areRecipientsValid = (value) => {
  const recipients = parseRecipientList(value);
  return recipients.length > 0 && recipients.every((email) => EMAIL_PATTERN.test(email));
};

export const areOptionalRecipientsValid = (value) => {
  const recipients = parseRecipientList(value);
  return recipients.every((email) => EMAIL_PATTERN.test(email));
};

export default function EmailRecipientsField({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  placeholder = 'name@company.com',
  helperText = 'Type an address and press Enter. Paste or add up to 25 recipients.'
}) {
  const [inputValue, setInputValue] = useState('');
  const recipients = useMemo(() => parseRecipientList(value), [value]);
  const invalidRecipients = recipients.filter((email) => !EMAIL_PATTERN.test(email));

  const publish = (next) => {
    onChange(parseRecipientList(next).join('; '));
  };

  const commitInput = () => {
    const pending = parseRecipientList(inputValue);
    if (!pending.length) return;
    publish([...recipients, ...pending]);
    setInputValue('');
  };

  return (
    <Autocomplete
      disabled={disabled}
      multiple
      freeSolo
      options={[]}
      value={recipients}
      inputValue={inputValue}
      onInputChange={(_, nextValue, reason) => {
        if (reason !== 'reset') setInputValue(nextValue);
      }}
      onChange={(_, nextValue) => publish(nextValue)}
      filterSelectedOptions
      clearOnBlur={false}
      renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
        <Chip
          {...getTagProps({ index })}
          key={option.toLowerCase()}
          label={option}
          size="small"
          color={EMAIL_PATTERN.test(option) ? 'primary' : 'error'}
          variant="outlined"
        />
      ))}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          label={label}
          placeholder={recipients.length ? 'Add another email' : placeholder}
          error={invalidRecipients.length > 0}
          helperText={invalidRecipients.length
            ? `Check: ${invalidRecipients.join(', ')}`
            : helperText}
          onBlur={commitInput}
          onPaste={(event) => {
            const pasted = event.clipboardData.getData('text');
            if (!/[;,\n]/.test(pasted)) return;
            event.preventDefault();
            publish([...recipients, ...parseRecipientList(pasted)]);
            setInputValue('');
          }}
          onKeyDown={(event) => {
            if (['Enter', ',', ';'].includes(event.key) && inputValue.trim()) {
              event.preventDefault();
              event.stopPropagation();
              commitInput();
            }
          }}
        />
      )}
      sx={{
        '& .MuiAutocomplete-inputRoot': {
          alignItems: 'center',
          py: 0.35
        }
      }}
    />
  );
}
